import { useMutation } from "@tanstack/react-query";

import { API_URL } from "@/constants/envVariables";
import { SESSION_EXPIRED_EVENT } from "@/constants/settings";
import { fetchApi } from "@/helpers/fetchApi";
import { getFilenameFromContentDisposition } from "@/helpers/getFilenameFromContentDisposition";
import { normalizeApiError } from "@/helpers/normalizeApiError";
import { saveFile } from "@/helpers/saveFile";
import { AppError } from "@/types";

import { RecoveryAuditReportSearchParams } from "./useRecoveryAuditReport";

export type RecoveryAuditExportParams = RecoveryAuditReportSearchParams & {
  transactionIds?: number[];
};

export const useRecoveryAuditReportExport = () => {
  return useMutation<void, AppError, RecoveryAuditExportParams>({
    mutationFn: async (params) => {
      const query = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
        sortBy: params.sortBy,
        sortDirection: params.sortDirection,
      });
      if (params.organizationName) query.set("organizationName", params.organizationName);
      if (params.accountId) query.set("accountId", params.accountId);
      if (params.status) query.set("status", params.status);
      if (params.eventType) query.set("eventType", params.eventType);
      if (params.from) query.set("from", params.from);
      if (params.to) query.set("to", params.to);
      if (params.transactionIds && params.transactionIds.length > 0) {
        query.set("transactionIds", params.transactionIds.join(","));
      }

      await fetchApi(
        `${API_URL}/reports/recovery/export?${query.toString()}`,
        {},
        {
          customCallback: async (response: Response) => {
            if (response.status === 401) {
              document.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
              return;
            }
            if (!response.ok) {
              const err = await response.json().catch(() => ({}));
              throw normalizeApiError(err);
            }

            const blob = await response.blob();
            const fallback = "recovery-history.csv";
            const filename = getFilenameFromContentDisposition(
              response.headers.get("Content-Disposition"),
              fallback,
            );
            saveFile({
              file: new File([blob], filename, { type: "text/csv" }),
              suggestedFileName: filename,
            });
          },
        },
      );
    },
  });
};
